import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';

import { Link, useHistory, useLocation } from 'react-router-dom';

import {
  HomeIcon, PersonAddIcon, RocketIcon
} from '@primer/octicons-react';

import classNames from 'classnames';
import filter from 'lodash/filter';
import isFunction from 'lodash/isFunction';
import remove from 'lodash/remove';

import { CounterLabel } from '@primer/react';
import sendEmailInvitations from '../../utils/sendEmailInvitations';
import useOnClickOutside from '../../utils/hooks/use_on_click_outside';
import { useUserContext } from '../contexts/UserData/UserData';
import { useWorkspaceContext } from '../workspaces/WorkspaceContext/WorkspaceContext';

import Avatar from '../common/Avatar/Avatar';
import BasicDropdown from '../common/BasicDropdown/BasicDropdown';
import Button from '../common/Button/Button';
import Icon from '../common/icons/Icon';
import Label from '../common/Labels/Label';
import ModalOverlay from '../common/ModalOverlay/ModalOverlay';
import UserInviteForm from '../users/UserInviteForm/UserInviteForm';
import WorkspaceIndex from '../workspaces/WorkspaceIndex/WorkspaceIndex';

import AdminIcon from '../common/icons/AdminIcon';
import CaretDownIcon from '../common/icons/CaretDownIcon';
import CaretUpIcon from '../common/icons/CaretUpIcon';
import ModelsIcon from '../common/icons/ModelsIcon';
import PipelineIcon from '../common/icons/PipelineIcon';
import WallarooIcon from '../common/icons/WallarooIcon';
import WorkspaceIcon from '../common/icons/WorkspaceIcon';

import { getThemeColor } from '../../utils/theme-utils';
import useClientContext from '../contexts/ClientData/ClientData';

type DisplayedWorkspaceType = {
  id: number,
  name: string
}

type WorkspaceDropdownType = {
  onViewWorkspacesClick: Function,
  isDisabled?: boolean
}

type HeaderLinkType = {
  className: string,
  children: React.ReactNode,
  currentPath: string,
  icon: any,
  to: string,
  useTailwindIconSizing?: boolean
}

type HeaderType = {
  clientName: string,
  userProfile: any,
  isAuthEnabled: boolean,
  isAdminDashboardEnabled: boolean,
  isAdminUser: boolean,
  areInvitesEnabled: boolean
}

const getWorkspaceDropdownValues = (userUUID: String, workspaces: any = [], currentWorkspace: any = undefined) => {
  const dup = [...workspaces];
  const myWorkspace = remove(dup, workspace => workspace.name.endsWith(' - Default Workspace') && workspace.createdBy === userUUID)[0];

  let displayedWorkspaces = workspaces.slice(0, 4);

  // We always expect to find this workspace.
  if (myWorkspace) {
    displayedWorkspaces = (!currentWorkspace || currentWorkspace.id === myWorkspace?.id) ? [myWorkspace, ...dup.slice(0, 4)] : [
      myWorkspace,
      currentWorkspace,
      ...filter(dup, workspace => workspace?.id !== currentWorkspace.id).slice(0, 3)
    ];
  }

  const workspaceOptions = displayedWorkspaces?.map(({ id, name }: DisplayedWorkspaceType) => ({
    id,
    label: myWorkspace?.id === id ? 'My Default Workspace' : name,
  }));

  const divider = (uniqueId: String) => ({ id: uniqueId, label: '' })

  const seeAllOption = {
    id: 'viewWorkspaces',
    label: <p>Manage Workspaces <CounterLabel>{workspaces?.length}</CounterLabel></p>,
  }

  return [...workspaceOptions, divider('manageWorkspaceDivider'), seeAllOption];
}

export const WorkspaceDropdown: React.FC<WorkspaceDropdownType & { [x: string]: any }> = ({
  onViewWorkspacesClick,
  isDisabled = false,
  ...rest
}) => {
  const {
    workspaces, currentWorkspace, currentWorkspaceId, setCurrentWorkspaceId
  } = useWorkspaceContext();
  const { userData }: any = useUserContext()
  const selectedWorkspaceId = currentWorkspaceId || null;

  const [selectedValue, setSelectedValue] = useState<number | string | null>(selectedWorkspaceId);
  const userUUID = userData?.id;
  const dropdownValues = useMemo(() => getWorkspaceDropdownValues(userUUID, workspaces, currentWorkspace), [userUUID, workspaces, currentWorkspace]);

  const handleJoinWorkspace = useCallback((workspaceId: number | string) => {
    if (setCurrentWorkspaceId) {
      setCurrentWorkspaceId(workspaceId);
      setSelectedValue(workspaceId);
    }
  }, [setCurrentWorkspaceId]);

  const handleValueSelection = useCallback((value: number | string) => {
    if (value === 'viewWorkspaces') {
      if (isFunction(onViewWorkspacesClick)) {
        onViewWorkspacesClick();
      }
    } else {
      handleJoinWorkspace(value);
    }
  }, [handleJoinWorkspace, onViewWorkspacesClick]);

  useEffect(() => {
    setSelectedValue(selectedWorkspaceId);
  }, [selectedWorkspaceId])

  if (!workspaces?.length) {
    return null;
  }

  return (
    <BasicDropdown
      className="ml-2"
      iconClass="xl:mr-2 lg:mr-2"
      bgColorClosed="brand"
      bgColorOpen="brand"
      textColor="white"
      direction="top-left"
      icon={WorkspaceIcon as any}
      dropdownLabel={null as any}
      selectedValue={selectedValue}
      values={dropdownValues}
      onValueSelected={handleValueSelection}
      isDisabled={isDisabled}
      hideBorder
      useTailwindIconSizing
      {...rest}
    />
  )
}

const AdminViewLabel = () => (
  <div className="flex ml-6">
    <Icon className="mr-2" size={32} icon={AdminIcon} useTailwindSizing />
    <Label
      className={classNames(
        'flex items-center justify-center',
      )}
      size="lg"
      color="white"
    >
      Admin View
    </Label>
  </div>
)

const HeaderLink: React.FC<HeaderLinkType> = ({
  className, children, currentPath, icon, to, useTailwindIconSizing = false,
}) => {
  const pathParts = currentPath.split('/');
  const toParts = to.split('/');
  const isCurrentRoute = currentPath === to || (pathParts?.length > 1 && toParts?.length > 1 && (pathParts[3] === toParts[3]));
  return (
    <Link
      className={
        classNames(
          className,
          'p-2 flex items-center justify-center',
          { 'rounded border-t-0 border-x-0 border-b-4 border-white': isCurrentRoute }
        )
      }
      to={to}
    >
      {
        icon ? (
          <Icon className="mr-1" size={32} icon={icon} useTailwindSizing={useTailwindIconSizing} />
        ) : null
      }
      <Label
        className={classNames(
          'flex items-center justify-center',
          { 'hidden sm:inline-block': !!icon },
        )}
        size="lg"
        color="white"
      >
        {children}
      </Label>
    </Link>
  )
}

export const Header: React.FC<HeaderType> = ({
  clientName,
  userProfile,
  isAdminUser = false,
  isAdminDashboardEnabled = false,
  isAuthEnabled = false,
  areInvitesEnabled = false,
}) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false);
  const [isInviteMenuOpen, setInviteMenuOpen] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const { logout, refetchUsers } = useUserContext();
  const { isOrchestrationEnabled } = useClientContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const location = useLocation();
  const history = useHistory();

  const pathname = location?.pathname;
  const { push: setRoute } = history;

  const isAdminPage = pathname?.includes('admin');

  const profileMenuRef = useRef(null);
  const workspaceMenuRef = useRef(null);
  const inviteMenuRef = useRef(null);
  useOnClickOutside(profileMenuRef, () => {
    setIsProfileMenuOpen(false);
  });

  useOnClickOutside(workspaceMenuRef, () => {
    setIsWorkspaceMenuOpen(false);
  });

  useOnClickOutside(inviteMenuRef, () => {
    setInviteMenuOpen(false);
  });

  const handleFormSubmission = useCallback((formData: any) => {
    sendEmailInvitations(formData).then(() => {
      setInvitationError(null);
      setInviteMenuOpen(false);

      setTimeout(refetchUsers, 1000);
    }).catch(() => {
      setInvitationError('Something went wrong with one of your email invitations.');
    });
  }, [refetchUsers])

  const routeToAdminDashboard = useCallback(() => {
    setIsProfileMenuOpen(false);
    setRoute('/admin/users');
  }, [setRoute])

  const routeToHomePage = useCallback(() => {
    setIsProfileMenuOpen(false);
    setRoute(`/workspaces/${currentWorkspaceId}`);
  }, [currentWorkspaceId, setRoute])

  return (
    <>
      <header className="bg-brand text-white flex justify-center items-center py-4 w-full">
        <div className="w-full flex justify-between items-center h-10 mx-4">
          <div className="flex justify-start items-center">
            <Link to={`/workspaces/${currentWorkspaceId}`} className="flex justify-start items-center">
              <Icon icon={WallarooIcon} size={48} useTailwindSizing />
              <h2 className="text-2xl hidden sm:inline-block sm:ml-2">{clientName}</h2>
            </Link>
            {
              !isAdminPage ? (
                <WorkspaceDropdown onViewWorkspacesClick={() => setIsWorkspaceMenuOpen(true)} data-testid="workspace-selector" />
              ) : (
                <AdminViewLabel />
              )
            }
          </div>
          <div className="flex items-center justify-start">
            {
              !isAdminPage && (
                <div className="flex items-center mr-2">
                  <HeaderLink to={`/workspaces/${currentWorkspaceId}`} currentPath={pathname} className="mr-4" icon={HomeIcon}>Home</HeaderLink>
                  {
                    isOrchestrationEnabled && (
                      <HeaderLink to={`/workspaces/${currentWorkspaceId}/orchestrations`} currentPath={pathname} className="mr-4" icon={RocketIcon}>Workloads</HeaderLink>
                    )
                  }
                  <HeaderLink to={`/workspaces/${currentWorkspaceId}/pipelines`} currentPath={pathname} className="mr-4" icon={PipelineIcon} useTailwindIconSizing>Pipelines</HeaderLink>
                  <HeaderLink to={`/workspaces/${currentWorkspaceId}/models`} currentPath={pathname} className="mr-4" icon={ModelsIcon} useTailwindIconSizing>Models</HeaderLink>
                </div>
              )
            }

            {isAuthEnabled && !!userProfile && (
              <>
                {areInvitesEnabled && <Button
                  variant="invisible"
                  onClick={() => setInviteMenuOpen(true)}
                  sx={{
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'white',
                    padding: 2,
                    boxShadow: 0,
                    fg: getThemeColor('brand'),
                    bg: getThemeColor('brand'),
                    ':hover:not([disabled])': {
                      bg: getThemeColor('brand'),
                    },
                    ':active:not([disabled])': {
                      bg: getThemeColor('brand'),
                    },
                  }}
                >
                  <div className="flex items-center">
                    <Icon fill="white" icon={PersonAddIcon} size={24} />
                    <Label className="ml-2 hover:text-border-light active:text-border-dark" color="white" bold>Invite Users</Label>
                  </div>
                </Button>}
                <div className="ml-4 relative" ref={profileMenuRef}>
                  <button
                    type="button"
                    id="profile-menu-toggle-button"
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex justify-center items-center"
                  >
                    <Avatar />
                    <div className="ml-2">
                      {isProfileMenuOpen ? <CaretUpIcon fill="white" /> : <CaretDownIcon fill="white" />}
                    </div>
                  </button>

                  {isProfileMenuOpen && (
                    <div
                      className="absolute z-50 bg-white rounded text-brand right-0 w-64 shadow-md border border-border-dark flex flex-col justify-start items-stretch"
                      style={{ top: 44 }}
                    >
                      <span className="py-2 px-4 truncate">
                        {userProfile.email || userProfile.username}
                      </span>
                      {
                        isAdminPage ? (
                          <>
                            <div className="h-px w-full bg-border-dark" />
                            <button type="button" onClick={routeToHomePage} className="py-2 px-4 text-left hover:bg-gray-200">
                              Platform User Dashboard
                            </button>
                          </>
                        ) : (
                          isAdminUser && isAdminDashboardEnabled && (
                            <>
                              <div className="h-px w-full bg-border-dark" />
                              <button type="button" onClick={routeToAdminDashboard} className="py-2 px-4 text-left hover:bg-gray-200">
                                Platform Admin Dashboard
                              </button>
                            </>
                          )
                        )
                      }
                      <div className="h-px w-full bg-border-dark" />
                      <button type="button" onClick={logout} className="py-2 px-4 text-left hover:bg-gray-200">
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      {
        isWorkspaceMenuOpen ? (
          <ModalOverlay
            animationKey="workspace-overlay"
            active={isWorkspaceMenuOpen}
            onModalToggle={setIsWorkspaceMenuOpen as any}
          >
            <div ref={workspaceMenuRef}>
              <div className="flex flex-col">
                <div className="mx-4 my-2 w-full flex flex-row-reverse">
                  <Button variant="close" iconSize={32} onClick={() => setIsWorkspaceMenuOpen(false)} /></div>
                <WorkspaceIndex
                  onCloseMenu={() => setIsWorkspaceMenuOpen(false)}
                />
              </div>
            </div>
          </ModalOverlay>
        ) : null
      }
      {
        isInviteMenuOpen ? (
          <ModalOverlay
            animationKey="user-invite-overlay"
            active={isInviteMenuOpen}
            onModalToggle={setInviteMenuOpen}
          >
            <div className="w-1/3 h-1/2" ref={inviteMenuRef}>
              <div className="flex flex-col">
                <div className="mx-4 my-2 w-full flex flex-row-reverse">
                  <Button variant="close" iconSize={32} onClick={() => setInviteMenuOpen(false)} />
                </div>
                <UserInviteForm error={invitationError} onFormSubmit={handleFormSubmission} />
              </div>
            </div>
          </ModalOverlay>
        ) : null
      }
    </>
  );
};

export default Header;
